/**
 * OnboardingPhone.jsx — s95 sign-up rebuild.
 *
 * Apple and Google sign-ups never had a mobile number on the account, so visit
 * reminders and text-message 2FA had nothing verified to use. This step asks for one
 * and verifies it before Security (so "text me a code" is a real choice there).
 * The server writes users.phone only after the code checks out
 * (POST /auth/phone/send-code + /auth/phone/verify; the account is the bearer's).
 * Shown only when the account has no phone — an email sign-up verified it already.
 * s95b: it is the first screen an Apple/Google patient sees, so it greets ("Hi, I'm
 * Orella."); it carries the SAME two controls as the email sign-up — the verbatim TCPA
 * opt-in for notification texts (components/SmsConsentText, recorded server-side) and the
 * Cloudflare Turnstile check before a code is texted — and it can be left (onExit).
 */
import { useState } from 'react'
import { useI18n } from '../../i18n'
import { authAPI } from '../../services/api'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import { apiErrorMessage } from '../../utils/apiErrorMessage'
import { useUserStore } from '../../stores'
import Turnstile from '../../components/Turnstile'
import SmsConsentText from '../../components/SmsConsentText'
import CurrentFactorField, { LockNotice, useLockCountdown } from '../../components/CurrentFactorField'
import { currentCodeMayBeSpent, currentCodeProblem, currentFactorRefusal, normalizeCurrentCode, refusalSentence } from '../../utils/currentFactor'
import {
  OnboardingShell,
  ScreenIntro,
  PrimaryButton,
  TextField,
  Footer,
  ScrollBody,
  useScrollToTop,
  TEXT_MUTED,
  ExitButton,
} from './onboardingPrimitives'
import orellaWordmarkBlue from '../../assets/logos/orella-wordmark-blue-transparent.png'

const digitsOf = (v) => (v || '').replace(/\D/g, '')
const usNumber = (v) => {
  const d = digitsOf(v)
  return d.length === 10 || (d.length === 11 && d.startsWith('1'))
}

export default function OnboardingPhone({ onDone, onExit }) {
  const { t } = useI18n()
  useScrollToTop('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [smsOptIn, setSmsOptIn] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileKey, setTurnstileKey] = useState(0)
  // ★★ PROD-1154 (the patient twin of PROD-1161): when the account signs in by SMS, moving that number ALSO needs
  //   a CURRENT code (utils/currentFactor.js), texted to the CURRENT number by CurrentFactorField (method="sms").
  //   Reactive: the API asks for it (403 2fa_current_factor_required, nothing used up) only for such an account and
  //   such a move, so authenticator-app and 2FA-off accounts never see the field. A refused current code comes
  //   AFTER the new number's code was approved and used up: that code is emptied and marked spent (codeSpent)
  //   until a new one is texted (send).
  const [currentAsk, setCurrentAsk] = useState(false)
  const [currentCode, setCurrentCode] = useState('')
  const [currentError, setCurrentError] = useState(null)
  const [codeSpent, setCodeSpent] = useState(false)
  const [lockSeconds, startLock] = useLockCountdown()

  // PROD-1154 (review L3): the API asks for a current code only when the SMS sign-in number MOVES. Edited back to
  //   the account's current number, the field and its code go away again (re-verifying the same number asks nothing).
  const changePhone = (v) => {
    setPhone(v)
    if (sent) { setSent(false); setCode(''); setCodeSpent(false) }
    const current = digitsOf(useUserStore.getState().user?.phone).slice(-10)
    if (currentAsk && current.length === 10 && digitsOf(v).slice(-10) === current) {
      setCurrentAsk(false); setCurrentCode(''); setCurrentError(null)
    }
  }

  const send = async () => {
    if (!usNumber(phone) || busy) return
    if (!turnstileToken) { setError(t('auth.verifyHuman', 'Please complete the verification below.')); return }
    setBusy(true); setError(null)
    try {
      await authAPI.phoneSendCode(phone, turnstileToken)
      setSent(true)
      setCode(''); setCodeSpent(false)
    } catch (e) {
      // PROD-807: sms_us_ca_only = nothing was sent; the server's sentence is the error and
      // `sent` stays as it was (no "code sent" state, no new resend affordance).
      setError(isSmsRefused(e) && smsRefusedSentence(e) ? smsRefusedSentence(e) : e?.error || t('onboarding.phone.sendFailed', 'We could not send a code to that number. Please check it and try again.'))
    } finally {
      // Turnstile tokens are single-use: a resend gets a fresh challenge.
      setTurnstileToken(null); setTurnstileKey((k) => k + 1)
      setBusy(false)
    }
  }

  const verify = async () => {
    if (!/^\d{6}$/.test(code) || busy || lockSeconds) return
    // PROD-1154: a new-number code a refused current code used up is not sent again (it cannot pass); the patient
    //   is told to get a new one (review L2), and the resend link is emphasised.
    if (codeSpent) { setError(t('onboarding.phone.codeUsed', 'That code was already used. Tap Resend Code to get a new one.')); return }
    const currentProblem = currentCodeProblem(currentAsk, currentCode)
    // Review L-1: an empty field gets no red line; the field's own grey line already says a code is needed.
    if (currentProblem) { setCurrentError(refusalSentence(t, currentProblem) || null); return }
    const sentCurrent = currentAsk ? normalizeCurrentCode(currentCode) : undefined
    setBusy(true); setError(null); setCurrentError(null)
    try {
      await authAPI.phoneVerify(phone, code, smsOptIn, sentCurrent)
      const e164 = `+1${digitsOf(phone).slice(-10)}`
      const st = useUserStore.getState()
      if (st.user) st.setUser({ ...st.user, phone: e164 })
      useUserStore.setState({ pendingProfile: { ...(st.pendingProfile || {}), phone: e164 } })
      onDone()
    } catch (e) {
      // PROD-1154: with a current code sent, any failure but the ones the API gives before it looks at that code
      //   (a wrong new-number code) may come after both codes were used: the new number's code was approved
      //   first. Both are emptied (review L1) and the new one is marked spent until a new one is texted.
      const spent = Boolean(sentCurrent && currentCodeMayBeSpent(e))
      if (spent) { setCurrentCode(''); setCode(''); setCodeSpent(true) }
      const refused = currentFactorRefusal(e)
      if (refused) {
        // PROD-1154: the screen stays on every current-factor refusal. Without a current code (required) nothing
        //   was checked or used up, and the field's own line explains what it wants.
        setCurrentAsk(true)
        if (refused.locked) startLock(refused.seconds)
        else setCurrentError(refusalSentence(t, refused.code) || null)
        // Review L-3: the new number's code went with the refused current code; say so now, not on the retry.
        if (spent) setError(t('onboarding.phone.codeUsed', 'That code was already used. Tap Resend Code to get a new one.'))
      } else if (e?.status === 0 || e?.status === 408 || e?.status >= 500) {
        // Review L-2: a dropped connection, a timeout or a server error is not a wrong code; say what happened.
        const what = apiErrorMessage(e, t('errors2.somethingWentWrong', 'Something went wrong. Please try again.'))
        // Review L-C: after a current code was sent, both codes were emptied above; say so here as well.
        setError(spent ? `${what} ${t('onboarding.phone.codeUsed', 'That code was already used. Tap Resend Code to get a new one.')}` : what)
      } else {
        setError(e?.error || t('onboarding.phone.badCode', 'That code did not match. Please try again.'))
      }
      setBusy(false)
    }
  }

  return (
    <OnboardingShell>
      <div className="px-5 flex items-center justify-between" style={{ paddingTop: 28, paddingBottom: 4 }}>
        <img src={orellaWordmarkBlue} alt="orella" style={{ height: 32, width: 'auto', display: 'block' }} />
        {onExit && <ExitButton onExit={onExit} />}
      </div>
      <ScreenIntro
        eyebrow={t('onboarding.phone.eyebrow', 'Your mobile')}
        title={t('onboarding.phone.title', 'Add your mobile number')}
        dialogue={t('onboarding.phone.dialogueGreet', 'Hi, Welcome to Orella! We will text visit reminders and sign-in codes to this number.')}
        required
      />
      <ScrollBody>
        <TextField
          label={t('onboarding.welcome.phone', 'Mobile')}
          value={phone}
          onChange={changePhone}
          placeholder="(555) 123-4567"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
        />
        {sent && (
          <TextField
            label={t('onboarding.phone.codeLabel', '6-digit code')}
            value={code}
            onChange={(v) => setCode(digitsOf(v).slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            hint={t('onboarding.phone.codeHint', 'We just texted it to you.')}
          />
        )}
        {/* PROD-1154: SMS sign-in only - a CURRENT code, texted to the current number (asked by the API's 403) */}
        {sent && currentAsk && (
          <div style={{ marginTop: 10 }} data-phone-current-code="1">
            <LockNotice seconds={lockSeconds} />
            {!lockSeconds && currentError && <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', marginBottom: 6 }}>{currentError}</p>}
            <CurrentFactorField
              value={currentCode}
              onChange={(v) => { setCurrentCode(v); setCurrentError(null) }}
              method="sms"
              disabled={busy || !!lockSeconds}
              autoFocus
              onEnter={() => { verify() }}
            />
          </div>
        )}
        <div className="flex items-start gap-2.5 cursor-pointer py-1" style={{ marginTop: 6 }}
          role="checkbox" aria-checked={smsOptIn} tabIndex={0}
          onClick={() => setSmsOptIn((v) => !v)}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setSmsOptIn((v) => !v) } }}>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
            style={{ borderRadius: 6, backgroundColor: smsOptIn ? '#46A8F9' : 'white', border: smsOptIn ? 'none' : '2px solid #CBD5E1' }}>
            {smsOptIn && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>}
          </div>
          <SmsConsentText />
        </div>
        {!sent && (
          <div className="flex justify-center" style={{ minHeight: 65, margin: '8px 0 4px' }}>
            <Turnstile
              key={turnstileKey}
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />
          </div>
        )}
        {error && <p role="alert" style={{ fontSize: 12.5, color: '#DC2626' }}>{error}</p>}
        {sent && turnstileToken === null && (
          <div className="flex justify-center" style={{ minHeight: 65, margin: '8px 0 4px' }}>
            <Turnstile
              key={`r${turnstileKey}`}
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />
          </div>
        )}
        {sent && (
          <button type="button" onClick={send} disabled={busy || !turnstileToken} data-phone-code-spent={codeSpent ? '1' : undefined}
            style={{ background: 'none', border: 0, color: codeSpent ? '#15354F' : TEXT_MUTED, fontWeight: codeSpent ? 600 : undefined, fontSize: 13, textDecoration: 'underline', padding: '6px 0' }}>
            {t('auth.resendCode')}
          </button>
        )}
      </ScrollBody>
      <Footer>
        {sent ? (
          <PrimaryButton onClick={verify} disabled={busy || !!lockSeconds || !/^\d{6}$/.test(code)}>
            {t('onboarding.phone.verify', 'Verify number')}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={send} disabled={busy || !usNumber(phone) || !turnstileToken}>
            {t('onboarding.phone.send', 'Text me a code')}
          </PrimaryButton>
        )}
      </Footer>
    </OnboardingShell>
  )
}
